/**
 * NRICField component.
 *
 * Password-style masked input for Singapore NRIC/FIN numbers or Passport.
 * Includes a type selector dropdown (NRIC / FIN / Passport) and a reveal toggle.
 * The value is cleared on form submission and no autocomplete is enabled.
 *
 * NRIC/FIN format: S/T/F/G/M + 7 digits + 1 letter (e.g., S1234567D)
 * Passport format: 6-12 alphanumeric characters (e.g., E12345678)
 */

import type { FormFieldDefinition } from "../../types";

/** SVG icon for the "show" (eye open) toggle */
const EYE_OPEN_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>`;

/** SVG icon for the "hide" (eye closed) toggle */
const EYE_CLOSED_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C11.74 7.13 12.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>`;

const ID_TYPE_OPTIONS = [
  { value: "NRIC", label: "NRIC" },
  { value: "FIN", label: "FIN" },
  { value: "Passport", label: "Passport (for tourist only)" },
];

export interface NRICFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
}

export class NRICField {
  private readonly element: HTMLElement;
  private readonly inputEl: HTMLInputElement;
  private readonly errorEl: HTMLElement;
  private readonly toggleBtn: HTMLButtonElement;
  private readonly typeSelectEl: HTMLSelectElement;
  private isRevealed = false;
  private selectedIdType: string;

  constructor(private readonly options: NRICFieldOptions) {
    const { field } = options;

    // Use id_type from field definition if provided
    this.selectedIdType = (field as any).id_type || "NRIC";

    this.element = document.createElement("div");
    this.element.className = "hana-form-field";

    // Label
    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);
    labelEl.textContent = "Identification";
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    // ID type selector
    this.typeSelectEl = document.createElement("select");
    this.typeSelectEl.className = "hana-form-field__id-type-select";
    this.typeSelectEl.setAttribute("aria-label", "Identification type");
    for (const opt of ID_TYPE_OPTIONS) {
      const optEl = document.createElement("option");
      optEl.value = opt.value;
      optEl.textContent = opt.label;
      this.typeSelectEl.appendChild(optEl);
    }
    this.typeSelectEl.value = this.selectedIdType;

    this.typeSelectEl.addEventListener("change", () => {
      this.selectedIdType = this.typeSelectEl.value;
      this._updateInputConstraints();
      this.clearError();
      this.options.onChange(field.name, this.inputEl.value);
    });

    this.element.appendChild(this.typeSelectEl);

    // Input wrapper for input + toggle button
    const wrapperEl = document.createElement("div");
    wrapperEl.className = "hana-form-field__nric-wrapper";

    // Input — password type by default, no autocomplete, no caching
    this.inputEl = document.createElement("input");
    this.inputEl.type = "password";
    this.inputEl.id = `hana-field-${field.name}`;
    this.inputEl.className =
      "hana-form-field__input hana-form-field__input--nric";
    this.inputEl.name = field.name;
    this.inputEl.setAttribute(
      "aria-label",
      "Identification number" + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.inputEl.setAttribute("aria-required", "true");
    }

    // Security: prevent browser from caching (M2 remediation)
    this.inputEl.autocomplete = "off";
    this.inputEl.setAttribute("autocomplete", "new-password");
    this.inputEl.setAttribute("data-lpignore", "true"); // LastPass
    this.inputEl.setAttribute("data-1p-ignore", "true"); // 1Password
    this.inputEl.setAttribute("data-form-type", "other"); // Dashlane

    this._updateInputConstraints();

    if (field.value) {
      this.inputEl.value = field.value;
    }

    this.inputEl.addEventListener("input", () => {
      // Uppercase the input for consistency
      this.inputEl.value = this.inputEl.value.toUpperCase();
      this.clearError();
      this.options.onChange(field.name, this.inputEl.value);
    });

    wrapperEl.appendChild(this.inputEl);

    // Reveal toggle button
    this.toggleBtn = document.createElement("button");
    this.toggleBtn.type = "button";
    this.toggleBtn.className = "hana-form-field__nric-toggle";
    this.toggleBtn.setAttribute("aria-label", "Show ID");
    this.toggleBtn.innerHTML = EYE_OPEN_SVG;

    this.toggleBtn.addEventListener("click", () => {
      this.toggleReveal();
    });

    wrapperEl.appendChild(this.toggleBtn);
    this.element.appendChild(wrapperEl);

    // Error message
    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getValue(): string {
    return this.inputEl.value.trim().toUpperCase();
  }

  /** Returns the selected identification type (NRIC, FIN, or Passport) */
  getIdType(): string {
    return this.selectedIdType;
  }

  /**
   * Clear the value from the input.
   * Called after form submission to ensure PII is never cached.
   */
  clearValue(): void {
    this.inputEl.value = "";
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.inputEl.classList.add("hana-form-field__input--error");
    this.inputEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.inputEl.classList.remove("hana-form-field__input--error");
    this.inputEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.inputEl.disabled = disabled;
    this.toggleBtn.disabled = disabled;
    this.typeSelectEl.disabled = disabled;
  }

  focus(): void {
    this.inputEl.focus();
  }

  /** Returns whether the ID is currently visible (not masked) */
  getRevealState(): boolean {
    return this.isRevealed;
  }

  private _updateInputConstraints(): void {
    if (this.selectedIdType === "Passport") {
      this.inputEl.placeholder = "e.g. E12345678";
      this.inputEl.maxLength = 12;
    } else {
      this.inputEl.placeholder = "e.g. S1234567D";
      this.inputEl.maxLength = 9;
    }
  }

  private toggleReveal(): void {
    this.isRevealed = !this.isRevealed;
    this.inputEl.type = this.isRevealed ? "text" : "password";
    this.toggleBtn.innerHTML = this.isRevealed ? EYE_CLOSED_SVG : EYE_OPEN_SVG;
    this.toggleBtn.setAttribute(
      "aria-label",
      this.isRevealed ? "Hide ID" : "Show ID",
    );
  }
}
